import { type ReactNode, useMemo } from 'react';

import { Flex } from '@/components/base';
import SelectDropdown from '@/components/Select/SelectDropdown';
import type { SelectOptionT } from '@/components/Select/types';
import { PropertyValueStyle } from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import IconTextProperty from '@/features/Commons/Label/IconTextProperty';
import useColor from '@/hooks/domain/useColor';
import { priorityMap } from '@/types/feedbackConst';

export interface Props {
  name: string;
  selected: number;
  useShortcut?: boolean;
  selectedNode?: ReactNode;
  onSelect: (key: PriorityKeyT, name?: string) => void;
  invisible?: boolean;
  pos?: PosOptionT;
}

const PriorityDropdown = ({
  name,
  selected,
  selectedNode,
  useShortcut,
  onSelect,
  invisible,
  pos,
}: Props) => {
  const color = useColor();
  const priorityOptions: SelectOptionT[] = Object.keys(priorityMap)
    .sort((a, b) => Number(b) - Number(a))
    .map((key) => {
      return {
        value: key,
        searchName: priorityMap[Number(key)].value,
      };
    });

  const priorityItem = useMemo(() => priorityMap[selected || 1], [selected]);

  return (
    <SelectDropdown
      width="150px"
      name={name}
      data={priorityOptions}
      searchOptions={{
        enable: true,
      }}
      mapper={(op) => {
        const Icon = priorityMap[Number(op.value) || 1].icon;
        return (
          <Flex g="8px" a="center" autoFocus={selected === +op.value}>
            {Icon}
            <Typo
              type="label3"
              color={color.gray800}
              style={{ lineHeight: '20px' }}
            >
              {priorityMap[Number(op.value)].value}
            </Typo>
          </Flex>
        );
      }}
      value={String(selected)}
      customMapper={() => {
        return invisible ? (
          <></>
        ) : selectedNode ? (
          selectedNode
        ) : (
          <PropertyValueStyle readOnly={false}>
            <IconTextProperty
              icon={priorityItem.icon}
              value={priorityItem.value}
            />
          </PropertyValueStyle>
        );
      }}
      onSelect={(key, name) => onSelect(key as PriorityKeyT, name)}
      shortcut={useShortcut ? 'KeyP' : ''}
    />
  );
};

export default PriorityDropdown;
